import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  SourceConflictResolution,
  SourceConflictResolutionResult,
  SourceConflictSummary,
} from '@agentwiki/shared'
import { apiClient, ApiError } from '../lib/api-client'

type ConflictListSummary = Pick<
  SourceConflictSummary,
  | 'id'
  | 'state'
  | 'reason'
  | 'detectedAt'
  | 'pendingExpiresAt'
  | 'incomingSourceContentHash'
  | 'incomingCompiledDocumentHash'
  | 'localDocumentHash'
>

export interface ConflictSourceItem {
  id: string
  documentId: string
  documentTitle: string
  sourceType: string
  sourcePath: string
  repoUrl: string | null
  branch: string | null
  commitSha: string | null
  syncStatus: string
  conflict?: ConflictListSummary
}

interface DocumentConflictResponse {
  source: ConflictSourceItem
  conflict: ConflictListSummary
}

export function sourceConflictErrorMessage(error: unknown) {
  if (error instanceof ApiError && error.status === 410) {
    return 'This pending conflict expired. Sync again to create a new conflict.'
  }
  if (error instanceof ApiError && error.status === 409) {
    return 'This conflict is stale or already resolved. Refresh the document before trying again.'
  }
  return 'AgentWiki could not resolve this conflict. No content was changed.'
}

export function useSourceConflictForDocument(documentId: string, enabled = true) {
  const lookup = useQuery<DocumentConflictResponse, ApiError>({
    queryKey: ['source-conflict-document', documentId],
    queryFn: () =>
      apiClient.get<DocumentConflictResponse>(`/api/sync/conflicts/document/${documentId}`),
    enabled,
  })
  const summary = lookup.data?.conflict
  const source = lookup.data ? { ...lookup.data.source, conflict: summary } : undefined
  const conflictId = summary?.id

  return {
    source,
    conflict: summary,
    conflictId,
    isLoading: lookup.isLoading,
    error: lookup.error?.status === 404 ? null : lookup.error,
    refetch: lookup.refetch,
  }
}

export function useResolveSourceConflict(documentId: string, conflictId?: string) {
  const queryClient = useQueryClient()
  return useMutation<SourceConflictResolutionResult, ApiError, SourceConflictResolution>({
    mutationFn: (decision) => {
      if (!conflictId) throw new ApiError(404, 'Conflict not found')
      return apiClient.post<SourceConflictResolutionResult>(
        `/api/sync/conflicts/${conflictId}/resolve`,
        { decision },
      )
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['documents'] }),
        queryClient.invalidateQueries({ queryKey: ['documents', documentId] }),
        queryClient.invalidateQueries({ queryKey: ['source-conflicts'] }),
        queryClient.invalidateQueries({ queryKey: ['source-conflict-document', documentId] }),
      ])
    },
  })
}
